// Screenshot capture. Two client-side strategies (ui-ux-plan / PRD §10):
//   'dom'     — SVG <foreignObject> rasterization: silent, no permission prompt,
//               respects redaction, imperfect fidelity (cross-origin images and
//               some effects won't render; canvas may taint → we degrade).
//   'display' — getDisplayMedia: pixel-perfect, permission prompt, opt-in.
// Both return a PNG data URL, or null on failure — capture never blocks a report.

const ALWAYS_REDACT = 'input[type="password"]'

/** Inline computed styles onto a clone so it renders standalone inside <foreignObject>. */
function inlineStyles(src: Element, dst: Element, redactSel: string): void {
  const srcEls = [src, ...src.querySelectorAll('*')]
  const dstEls = [dst, ...dst.querySelectorAll('*')]
  for (let i = 0; i < srcEls.length; i++) {
    const s = srcEls[i]
    const d = dstEls[i] as HTMLElement
    if (!(d instanceof HTMLElement)) continue
    const cs = getComputedStyle(s)
    let css = ''
    for (const prop of cs) css += `${prop}:${cs.getPropertyValue(prop)};`
    d.setAttribute('style', css)
    if ((s as Element).matches?.(redactSel)) {
      d.textContent = '•••'
      d.style.filter = 'blur(4px)'
    }
    // Drop elements that can't cross into foreignObject cleanly.
    if (d.tagName === 'IMG' && !sameOrigin((s as HTMLImageElement).src)) {
      d.style.visibility = 'hidden'
    }
  }
}

function sameOrigin(url: string): boolean {
  try {
    return new URL(url, location.href).origin === location.origin
  } catch {
    return false
  }
}

/**
 * Rasterize a viewport region [x,y,w,h] (CSS px) to a canvas via foreignObject.
 * Returns a canvas positioned so region-local (0,0) is the region's top-left.
 */
export async function rasterizeRegion(
  x: number,
  y: number,
  w: number,
  h: number,
  redact: string[] = [],
): Promise<HTMLCanvasElement | null> {
  if (w <= 0 || h <= 0) return null
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const redactSel = [ALWAYS_REDACT, ...redact].join(', ')

  const clone = document.body.cloneNode(true) as HTMLElement
  // Remove our own overlay from the capture.
  clone.querySelectorAll('qc-lens').forEach((n) => n.remove())
  inlineStyles(document.body, clone, redactSel)
  clone.style.margin = '0'

  const bodyRect = document.body.getBoundingClientRect()
  // Position the cloned body so the requested region lands at 0,0.
  const offsetX = -(x - bodyRect.left)
  const offsetY = -(y - bodyRect.top)

  const xml = new XMLSerializer().serializeToString(clone)
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">` +
    `<foreignObject x="${offsetX}" y="${offsetY}" width="${bodyRect.width}" height="${bodyRect.height}">` +
    `<div xmlns="http://www.w3.org/1999/xhtml">${xml}</div>` +
    `</foreignObject></svg>`

  const img = new Image()
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('svg image load failed'))
      img.src = url
    })
  } catch {
    return null
  }
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(dpr, dpr)
  ctx.fillStyle = getComputedStyle(document.body).backgroundColor || '#fff'
  ctx.fillRect(0, 0, w, h)
  try {
    ctx.drawImage(img, 0, 0)
  } catch {
    return null
  }
  return canvas
}

/** PNG data URL for a canvas, or null if the canvas is tainted. */
export function canvasToDataUrl(canvas: HTMLCanvasElement): string | null {
  try {
    return canvas.toDataURL('image/png')
  } catch {
    return null // cross-origin taint — caller keeps vectors, drops the image
  }
}

/** Element screenshot (dom strategy). */
export async function captureElement(el: Element, redact: string[]): Promise<string | null> {
  const r = el.getBoundingClientRect()
  const canvas = await rasterizeRegion(r.left, r.top, r.width, r.height, redact)
  return canvas ? canvasToDataUrl(canvas) : null
}

/**
 * Region screenshot with annotation ink composited on top (marker tool).
 * @param strokes stroke paths in *page* coords; converted to region-local.
 */
export async function captureAnnotatedRegion(
  box: { x: number; y: number; w: number; h: number },
  strokesPage: Array<Array<{ x: number; y: number }>>,
  redact: string[],
): Promise<string | null> {
  // box is in page coords; convert to viewport for rasterization.
  const vx = box.x - window.scrollX
  const vy = box.y - window.scrollY
  const canvas = await rasterizeRegion(vx, vy, box.w, box.h, redact)
  if (!canvas) return null
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    ctx.save()
    ctx.scale(dpr, dpr)
    drawStrokes(ctx, strokesPage, box.x, box.y)
    ctx.restore()
  }
  return canvasToDataUrl(canvas)
}

function drawStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: Array<Array<{ x: number; y: number }>>,
  originX: number,
  originY: number,
): void {
  ctx.lineWidth = 3
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const stroke of strokes) {
    if (!stroke.length) continue
    // white halo then accent, matching the live ink.
    for (const [color, width] of [
      ['rgba(255,255,255,0.9)', 5],
      ['#38BDF8', 3],
    ] as const) {
      ctx.strokeStyle = color
      ctx.lineWidth = width
      ctx.beginPath()
      ctx.moveTo(stroke[0].x - originX, stroke[0].y - originY)
      for (const pt of stroke.slice(1)) ctx.lineTo(pt.x - originX, pt.y - originY)
      ctx.stroke()
    }
  }
}

/** Display-capture strategy: one-frame grab from getDisplayMedia, cropped to region. */
export async function captureDisplayRegion(box: {
  x: number
  y: number
  w: number
  h: number
}): Promise<string | null> {
  const md = navigator.mediaDevices as MediaDevices & {
    getDisplayMedia?: (c?: unknown) => Promise<MediaStream>
  }
  if (!md?.getDisplayMedia) return null
  let stream: MediaStream | null = null
  try {
    stream = await md.getDisplayMedia({ video: { frameRate: 1 } } as MediaStreamConstraints)
    const track = stream.getVideoTracks()[0]
    const video = document.createElement('video')
    video.srcObject = stream
    await video.play()
    await new Promise((r) => setTimeout(r, 120))
    const canvas = document.createElement('canvas')
    canvas.width = box.w
    canvas.height = box.h
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    // Best-effort crop assuming the captured surface maps 1:1 to the viewport.
    ctx.drawImage(video, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h)
    track.stop()
    return canvasToDataUrl(canvas)
  } catch {
    return null
  } finally {
    stream?.getTracks().forEach((t) => t.stop())
  }
}
